import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram, Twitter, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { COMPANY, LOGO_URL, waLink } from "@/lib/constants";

const QUICK = [
    { l: "Home", to: "/" },
    { l: "About", to: "/#about" },
    { l: "Services", to: "/#services" },
    { l: "Industries", to: "/#industries" },
    { l: "Contact", to: "/#contact" },
];

const PRODUCTS_COL = [
    "Enterprise Switches", "Core Switches", "Routers", "Firewalls",
    "Wireless Access Points", "NAS Storage", "Rack Servers", "UPS Systems",
];

const SERVICES_COL = [
    "Network Design", "Structured Cabling", "Fiber Optic Installation",
    "Wi-Fi Deployment", "Data Center Setup", "AMC & Support",
];

export default function Footer() {
    return (
        <footer className="bg-navy text-white relative overflow-hidden" data-testid="site-footer">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                 style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1E90FF 0, transparent 40%), radial-gradient(circle at 80% 60%, #1E90FF 0, transparent 40%)" }} />
            <div className="container-lg relative py-16 lg:py-20">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="inline-flex items-center rounded-2xl bg-white px-6 py-5 shadow-[0_8px_30px_rgba(0,0,0,0.35)]">
                            <img src={LOGO_URL} alt={COMPANY.name} className="h-24 sm:h-28 lg:h-32 w-auto" />
                        </div>
                        <p className="mt-6 text-sm text-slate-300 leading-relaxed max-w-sm">
                            CoreCyrus is a trusted enterprise networking partner delivering end-to-end
                            infrastructure — design, deployment, AMC and support — across India.
                        </p>
                        <div className="mt-6 flex items-center gap-2">
                            {[Linkedin, Twitter, Facebook, Instagram].map((Icon, i) => (
                                <a
                                    key={i}
                                    href="#"
                                    aria-label="social"
                                    data-testid={`footer-social-${i}`}
                                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand flex items-center justify-center transition-colors"
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                        <a
                            href={waLink()}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-whatsapp"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                        </a>
                    </div>

                    <FooterCol title="Quick Links">
                        {QUICK.map((q) => (
                            <li key={q.l}>
                                <Link to={q.to} className="hover:text-brand transition-colors" data-testid={`footer-quick-${q.l.toLowerCase()}`}>
                                    {q.l}
                                </Link>
                            </li>
                        ))}
                    </FooterCol>

                    <FooterCol title="Products">
                        {PRODUCTS_COL.map((p) => (
                            <li key={p}>
                                <Link to="/products" className="hover:text-brand transition-colors">
                                    {p}
                                </Link>
                            </li>
                        ))}
                    </FooterCol>

                    <FooterCol title="Services">
                        {SERVICES_COL.map((s) => (
                            <li key={s}>
                                <Link to="/#services" className="hover:text-brand transition-colors">
                                    {s}
                                </Link>
                            </li>
                        ))}
                    </FooterCol>

                    <div className="lg:col-span-3">
                        <h4 className="font-display font-bold text-white uppercase text-xs tracking-[0.24em]">Contact</h4>
                        <ul className="mt-5 space-y-4 text-sm text-slate-300">
                            <li className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                                <span>
                                    {COMPANY.address.line1}, {COMPANY.address.line2},{" "}
                                    {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.zip}
                                </span>
                            </li>
                            <li className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-brand" />
                                <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-brand transition-colors">{COMPANY.phone}</a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-brand" />
                                <a href={`mailto:${COMPANY.email}`} className="hover:text-brand transition-colors">{COMPANY.email}</a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
                    <p>© {new Date().getFullYear()} {COMPANY.name}. {COMPANY.tagline}</p>
                    <div className="flex items-center gap-6">
                        <Link to="/#privacy" className="hover:text-brand transition-colors">Privacy Policy</Link>
                        <Link to="/#terms" className="hover:text-brand transition-colors">Terms &amp; Conditions</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, children }) {
    return (
        <div className="lg:col-span-2">
            <h4 className="font-display font-bold text-white uppercase text-xs tracking-[0.24em]">{title}</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-300">{children}</ul>
        </div>
    );
}
